import React from 'react';
import { ORDER_STATES } from '../../assets/data/arrays';
import {
  ROLE_ID_ADMIN,
  ROLE_ID_AGENTE_VENTAS,
  ROLE_ID_CONTABILIDAD,
  ROLE_ID_GESTOR_GARANTIAS,
  ROLE_ID_LIDER_VENTAS,
  ROLE_ID_OPERACIONES,
} from '../../constants/roleIds';
import { InformationCircleIcon, TagIcon } from '@heroicons/react/24/outline';
import { Input, Select } from '../ui';
import { FormField } from '../ui/Form';

interface StatusSelectorProps {
  value: string;
  onChange: (value: string) => void;
  mode: 'view' | 'edit' | 'create';
  userRoleId: number;
  recogeEnBodega: boolean;
  currentStatus?: string;
  paymentMethod?: string;
  errors?: { [key: string]: string };
  disabled?: boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({
  value,
  onChange,
  mode,
  userRoleId,
  recogeEnBodega,
  currentStatus,
  paymentMethod,
  errors,
  disabled = false
}) => {
  const normalizedPaymentMethod = (paymentMethod || '').toString().trim().toLowerCase()

  // Función para determinar si un estado debe mostrarse
  const shouldShowState = (estadoValue: string) => {
    if (normalizedPaymentMethod === 'plan_separe') {
      return estadoValue === 'verify_transaction' || estadoValue === 'abonado'
    }

    // Devolución de dinero: solo admin (1), contabilidad (19), gestor garantías (21)
    if (estadoValue === 'devolucion_dinero') {
      if (![ROLE_ID_ADMIN, ROLE_ID_CONTABILIDAD, ROLE_ID_GESTOR_GARANTIAS].includes(userRoleId)) return false
      if (userRoleId === ROLE_ID_ADMIN && (normalizedPaymentMethod === 'cod' || normalizedPaymentMethod === 'cmdm' || normalizedPaymentMethod === 'addi')) {
        return false
      }
      return true
    }

    // Admin ve todos los estados (salvo cuando el método restringe a un solo estado)
    if (userRoleId === ROLE_ID_ADMIN) {
      // Recaudo Contra Entrega (cod), Crédito MDM (cmdm) y Crédito ADDI (addi): solo estado "processing"
      if (normalizedPaymentMethod === 'cod' || normalizedPaymentMethod === 'cmdm' || normalizedPaymentMethod === 'addi') {
        return estadoValue === 'processing';
      }
      return true;
    }

    // Recaudo Contra Entrega (cod), Crédito MDM (cmdm) y Crédito ADDI (addi): solo estado "processing"
    if (normalizedPaymentMethod === 'cod' || normalizedPaymentMethod === 'cmdm' || normalizedPaymentMethod === 'addi') {
      return estadoValue === 'processing';
    }

    // Agente de ventas (rol 7) y Líder de ventas (rol 6) con método de pago Bold:
    // solo debe poder usar "Pendiente de pago" (pending)
    if ((userRoleId === ROLE_ID_AGENTE_VENTAS || userRoleId === ROLE_ID_LIDER_VENTAS) && normalizedPaymentMethod === 'bold') {
      return estadoValue === 'pending';
    }
    
    // Si el método de pago es "cod" (contra entrega), NO permitir "verify_transaction"
    if (normalizedPaymentMethod === 'cod' && estadoValue === 'verify_transaction') {
      return false;
    }
    
    // Si recoge en bodega está activado
    if (recogeEnBodega) {
      // Líder de ventas: puede ver "Verificar transacción" en modo create
      if (userRoleId === ROLE_ID_LIDER_VENTAS && mode === 'create') {
        if (estadoValue === 'verify_transaction') {
          return true;
        }
      }
      
      // Agente de ventas: puede ver "Asignado" y "Verificar transacción"
      if (userRoleId === ROLE_ID_AGENTE_VENTAS) {
        if (/* estadoValue === 'asignado' || */ estadoValue === 'verify_transaction') {
          return true;
        }
      } 
      // Rol 16: puede ver "Asignado" y "Verificar transacción"
     if (userRoleId === ROLE_ID_OPERACIONES) {
       if (estadoValue === 'asignado' || estadoValue === 'verify_transaction') {
         return true;
       }
     }
      
      // Agente de ventas en modo edit con estado "enviado": puede ver "Completado" y "Verificar transacción"
      if (userRoleId === ROLE_ID_AGENTE_VENTAS && mode === 'edit' && currentStatus === 'enviado') {
        if (estadoValue === 'completed' || estadoValue === 'verify_transaction') {
          return true;
        }
      }
      
      // Para otros roles, mostrar solo "Verificar transacción" cuando recoge en bodega está activo
      if (estadoValue === 'verify_transaction') {
        return true;
      }
      return false;
    }
    
    // Estados básicos para usuarios normales (cuando NO recoge en bodega)
    const estadosBasicos = ['processing', 'verify_transaction'];
    // Líder de ventas (Rol 6): Puede ver "processing" solo si el método de pago es "cod"
    if (userRoleId === ROLE_ID_LIDER_VENTAS) {
      if (normalizedPaymentMethod === 'cod' && estadoValue === 'processing') {
        return true;
      }
      return ['verify_transaction'].includes(estadoValue);
    }
    // Agente de ventas (Rol 7): Puede ver "processing" solo si el método de pago es "cod"
    if (userRoleId === ROLE_ID_AGENTE_VENTAS) {
      if (normalizedPaymentMethod === 'cod' && estadoValue === 'processing') {
        return true;
      }
      return ['verify_transaction'].includes(estadoValue);
    }


    // Rol 16 puede ver "asignado" siempre
    if (userRoleId === ROLE_ID_OPERACIONES) {
      return ['processing','asignado'].includes(estadoValue);
    }

    return estadosBasicos.includes(estadoValue);
  };

  // Filtrar estados según las condiciones y ordenar alfabéticamente por etiqueta
  const availableStates = ORDER_STATES.filter(estado => shouldShowState(estado.value));
  const sortedStates = [...availableStates].sort((a, b) =>
    a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
  );

  // Convertir estados a formato SelectOption
  const selectOptions = [
    { value: '', label: 'Seleccione' },
    ...sortedStates.map((estado) => ({
      value: estado.value,
      label: estado.label
    }))
  ];
  
  return (
    <FormField
      label="Estado"
      required
      error={errors?.status}
      icon={TagIcon}
      labelClassName="flex items-center gap-1"
    >
      {mode === 'view' ? (
        <Input
          type="text"
          value={value || ''}
          readOnly
          mode="view"
        />
      ) : (
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          options={selectOptions}
          placeholder="Seleccione"
          error={!!errors?.status}
          errorMessage={errors?.status}
          mode={mode}
        />
      )}
      
      {(userRoleId === ROLE_ID_AGENTE_VENTAS || userRoleId === ROLE_ID_LIDER_VENTAS) && normalizedPaymentMethod === 'cod' && !recogeEnBodega && (
        <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
          <InformationCircleIcon className="w-4 h-4 text-blue-600" />
          <span>Procesando disponible</span>
        </div>
      )}
    </FormField>
  );
};

export default StatusSelector;